<script setup>
import { ref, computed, watch } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useContactInboxes } from 'dashboard/composables/useContactInboxes';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ContactsForm from 'dashboard/components-next/Contacts/ContactsForm/ContactsForm.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const emit = defineEmits(['create']);

const { t } = useI18n();

// El alta pide sólo lo imprescindible: nombre, apellido, celular, correo y
// empresa. Ciudad, país, biografía y redes se completan luego en la ficha.
const CREATE_FIELDS = [
  'FIRST_NAME',
  'LAST_NAME',
  'PHONE_NUMBER',
  'EMAIL_ADDRESS',
  'COMPANY_NAME',
];

const dialogRef = ref(null);
const contactsFormRef = ref(null);
const contact = ref(null);

const store = useStore();
const uiFlags = useMapGetter('contacts/getUIFlags');
const isCreatingContact = computed(() => uiFlags.value.isCreating);

// El contacto se guarda en un canal: ahí se le abre su bandeja y por ahí se
// le podrá escribir. Se propone el canal por defecto de la cuenta y se puede
// cambiar en el momento.
const { availableInboxes, defaultInboxId, missingFieldFor } =
  useContactInboxes();
const selectedInboxId = ref(null);
const isPickingInbox = ref(false);

watch(
  defaultInboxId,
  value => {
    if (selectedInboxId.value === null) selectedInboxId.value = value;
  },
  { immediate: true }
);

const selectedInbox = computed(
  () =>
    availableInboxes.value.find(inbox => inbox.id === selectedInboxId.value) ||
    null
);

// El alta va en una transacción: si el canal exige un dato que falta, no se
// crea el contacto a medias, no se crea nada. Mejor avisar antes.
const missingField = computed(() =>
  missingFieldFor(selectedInbox.value, contact.value || {})
);

const canSave = computed(
  () => !contactsFormRef.value?.isFormInvalid && !missingField.value
);

const pickInbox = id => {
  isPickingInbox.value = false;
  selectedInboxId.value = id;
};

const createNewContact = contactItem => {
  contact.value = contactItem;
};

const handleDialogConfirm = async () => {
  if (!contact.value || missingField.value) return;
  emit('create', {
    ...contact.value,
    ...(selectedInboxId.value ? { inboxId: selectedInboxId.value } : {}),
  });
};

const onSuccess = () => {
  contactsFormRef.value?.resetForm();
  selectedInboxId.value = defaultInboxId.value;
  dialogRef.value.close();
};

const closeDialog = () => {
  dialogRef.value.close();
};

const open = () => {
  if (!availableInboxes.value.length) store.dispatch('inboxes/get');
  dialogRef.value?.open();
};

defineExpose({ dialogRef, contactsFormRef, onSuccess, open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="lg"
    overflow-y-auto
    @confirm="handleDialogConfirm"
  >
    <ContactsForm
      ref="contactsFormRef"
      is-new-contact
      hide-social-profiles
      :fields="CREATE_FIELDS"
      :title="
        t('CONTACTS_LAYOUT.HEADER.ACTIONS.CONTACT_CREATION.NEW_CONTACT_TITLE')
      "
      @update="createNewContact"
    />
    <!-- Dónde se guarda: se dice antes de guardar, no después -->
    <div
      v-if="availableInboxes.length"
      class="flex flex-col gap-1.5 pt-4 mt-4 border-t border-n-weak"
    >
      <div class="relative flex flex-wrap items-center gap-2">
        <span class="text-[13px] text-n-slate-11">
          {{ t('CONTACTS_LAYOUT.HEADER.ACTIONS.CONTACT_CREATION.SAVED_IN') }}
        </span>
        <button
          v-on-clickaway="() => (isPickingInbox = false)"
          type="button"
          class="inline-flex items-center h-8 gap-1.5 px-2.5 text-xs font-medium transition-colors border rounded-lg border-n-container bg-n-solid-1 hover:bg-n-alpha-1 text-n-slate-12"
          @click="isPickingInbox = !isPickingInbox"
        >
          <Icon icon="i-lucide-inbox" size="size-3.5 opacity-70" />
          <span class="truncate max-w-[12rem]">
            {{
              selectedInbox?.name ||
              t('CONTACTS_LAYOUT.HEADER.ACTIONS.CONTACT_CREATION.NO_CHANNEL')
            }}
          </span>
          <Icon icon="i-lucide-chevron-down" size="size-3 opacity-55" />
        </button>
        <div
          v-if="isPickingInbox"
          class="absolute z-30 top-full mt-1.5 ltr:left-0 rtl:right-0 min-w-[238px] pt-1 pb-1.5 rounded-[10px] border border-n-weak bg-n-solid-2 shadow-[0_10px_28px_rgba(20,40,60,.15)] max-h-64 overflow-y-auto"
        >
          <button
            type="button"
            class="flex items-center w-full h-[31px] px-3 text-[12.5px] text-left text-n-slate-11 font-medium hover:bg-n-alpha-1"
            @click="pickInbox(null)"
          >
            {{
              t('CONTACTS_LAYOUT.HEADER.ACTIONS.CONTACT_CREATION.NO_CHANNEL')
            }}
          </button>
          <button
            v-for="inbox in availableInboxes"
            :key="inbox.id"
            type="button"
            class="flex items-center gap-2 w-full h-[31px] px-3 text-[12.5px] text-left hover:bg-n-alpha-1"
            :class="
              inbox.id === selectedInboxId
                ? 'bg-n-blue-2 text-n-blue-text font-semibold'
                : 'text-n-slate-11 font-medium'
            "
            @click="pickInbox(inbox.id)"
          >
            <span class="flex-1 truncate">{{ inbox.name }}</span>
            <Icon
              v-if="inbox.id === selectedInboxId"
              icon="i-lucide-check"
              size="size-3 text-n-blue-text"
            />
          </button>
        </div>
      </div>
      <p v-if="missingField" class="mb-0 text-xs text-n-ruby-11">
        {{
          t(
            `CONTACTS_LAYOUT.HEADER.ACTIONS.CONTACT_CREATION.MISSING_${missingField === 'email' ? 'EMAIL' : 'PHONE'}`,
            { channel: selectedInbox?.name }
          )
        }}
      </p>
    </div>

    <template #footer>
      <div class="flex items-center justify-between w-full gap-3">
        <Button
          :label="t('DIALOG.BUTTONS.CANCEL')"
          variant="link"
          type="reset"
          class="h-10 hover:!no-underline hover:text-n-brand"
          @click="closeDialog"
        />
        <Button
          type="submit"
          :label="
            t('CONTACTS_LAYOUT.HEADER.ACTIONS.CONTACT_CREATION.SAVE_CONTACT')
          "
          color="blue"
          :disabled="!canSave"
          :is-loading="isCreatingContact"
        />
      </div>
    </template>
  </Dialog>
</template>
